import Button from '@/components/Button';
import Checkbox from '@/components/Form/Checkbox';
import PrivacyPolicyDialog from '@/components/GlobalDialog/PrivacyPolicy';
import TermsConditionsDialog from '@/components/GlobalDialog/TermsConditionsDialog';
import Image from '@/components/Image';
import { useMemo, useState } from 'react';
import { env } from '@/utils/env';
import { useStore } from '@/providers/app-store-provider';
import { useTranslations } from 'next-intl';
import { IS_DE_FR } from '@/utils/constants';

export default function PlaceOrderButton({
  goToPlaceOrder,
  paymentType,
  placeOrderDisable,
  privacyPolicy,
  onPrivacyPolicyChange,
  loading,
}: {
  goToPlaceOrder: () => void;
  paymentType: string;
  placeOrderDisable: boolean;
  privacyPolicy: boolean;
  onPrivacyPolicyChange: (checked: boolean) => void;
  loading: boolean;
}) {
  const t = useTranslations('payment.PlaceOrderButton');

  const [privacyPolicyDialogShow, setPrivacyPolicyDialogShow] = useState(false);
  const [termsConditionsDialogShow, setTermsConditionsDialogShow] = useState(false);

  const isPaypal = useMemo(() => {
    return paymentType === 'paypal';
  }, [paymentType]);

  const isUSSite = env.NEXT_PUBLIC_SITE_NAME === 'us';

  const userInfo = useStore((state) => state.userInfo);

  // 检查美国站用户是否已经同意过短信营销协议
  // 优先使用账户信息接口返回的值
  const hasUSSMSConsent = useMemo(() => {
    if (!isUSSite) return null;

    // 优先使用账户信息接口返回的值
    if (userInfo?.order_payment_marketing_notification === 1) {
      return true; // 已勾选
    }
    if (userInfo?.order_payment_marketing_notification === 2) {
      return false; // 未勾选
    }

    return false; // 默认未勾选
  }, [isUSSite, userInfo]);

  // 如果美国站用户已经同意过，则不显示协议项
  const shouldShowUSSMSCheckbox = isUSSite && hasUSSMSConsent === false;

  return (
    <>
      <div>
        <div className="h-[57px] mt-18 relative">
          <div id="paypal-btn" className="w-full h-full overflow-hidden rounded-[14px]" />
          <Button
            id="right-button-offset-height"
            onClick={() => {
              if (placeOrderDisable || loading) {
                return;
              }
              goToPlaceOrder();
            }}
            loading={loading}
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: '100%',
              height: '100%',
              bgcolor: placeOrderDisable ? '#ccc' : '#463AE8',
              borderRadius: '10px',
              color: 'white',
              fontSize: '20px',
              fontFamily: 'var(--font-bricolage-bold)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 998,
              cursor: placeOrderDisable ? 'not-allowed' : 'pointer',
              border: placeOrderDisable ? 'none' : undefined,
              pointerEvents: isPaypal ? 'none' : undefined,
              '&:hover': {
                bgcolor: placeOrderDisable ? '#ccc' : '#463AE8',
              },
            }}
          >
            {t('placeOrder')}
          </Button>
        </div>
        <div className="mt-16">
          {shouldShowUSSMSCheckbox && (
            <Checkbox
              label={
                <div className="text-[14px] leading-[17px] text-[#808080]">
                  I would like to receive texts from Zeelool (Yes, I agree to these terms). By
                  checking this box, I provide my signature consenting to receive recurring
                  automated promotional and personalized marketing text messages from Zeelool at the
                  cell number used when signing up. Consent is not a condition of any purchase.
                  Reply HELP for help and STOP to cancel. Msg frequency varies. Msg & data rates may
                  apply.
                </div>
              }
              checked={privacyPolicy}
              onChange={(e) => {
                onPrivacyPolicyChange(e.target.checked);
              }}
              sx={{
                alignItems: 'flex-start',
                '& .MuiFormControlLabel-label': {
                  marginTop: 0,
                },
                '.MuiCheckbox-root': {
                  paddingTop: '0 !important',
                },
              }}
            />
          )}
          {!isUSSite && (
            <Checkbox
              label={
                <div className="text-12 leading-[16px]">
                  {t('iAgreeWithThe')}{' '}
                  <span
                    className="underline cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setTermsConditionsDialogShow(true);
                    }}
                  >
                    {t('termsAndConditions')}
                  </span>{' '}
                  {t('and')}{' '}
                  <span
                    className="underline cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setPrivacyPolicyDialogShow(true);
                    }}
                  >
                    {t('privacyPolicy')}
                  </span>
                </div>
              }
              checked={privacyPolicy}
              onChange={(e) => {
                onPrivacyPolicyChange(e.target.checked);
              }}
            />
          )}
        </div>
        <Image
          src={IS_DE_FR ? `/payment/quanyi{locale}.png` : '/payment/quanyi.png'}
          alt="privacy policy"
          unoptimized
          width={360}
          height={40}
          className="w-full mt-20"
        />
        {shouldShowUSSMSCheckbox && (
          <div className="text-[14px] leading-[17px] text-[#808080] mt-20">
            By proceeding to payment you agree to Zeelool&apos;s{' '}
            <span
              className="underline cursor-pointer text-[#282828]"
              onClick={() => setTermsConditionsDialogShow(true)}
            >
              Conditions of Use & Sale
            </span>
            ,{' '}
            <span
              className="underline cursor-pointer text-[#282828]"
              onClick={() => setPrivacyPolicyDialogShow(true)}
            >
              Privacy Notice
            </span>{' '}
            and{' '}
            <span
              className="underline cursor-pointer text-[#282828]"
              onClick={(event) => {
                if (env.NEXT_PUBLIC_ENABLE_ONETRUST === 'true') {
                  event.preventDefault();
                  window?.OneTrust?.ToggleInfoDisplay();
                }
              }}
            >
              Cookies & Internet Advertising
            </span>
            , and consent to receive email with news and offers about our products.
          </div>
        )}
      </div>

      {privacyPolicyDialogShow && (
        <PrivacyPolicyDialog
          show={privacyPolicyDialogShow}
          onClose={() => setPrivacyPolicyDialogShow(false)}
        />
      )}
      {termsConditionsDialogShow && (
        <TermsConditionsDialog
          show={termsConditionsDialogShow}
          onClose={() => setTermsConditionsDialogShow(false)}
        />
      )}
    </>
  );
}
